/**
 * Account-creation logic for screens/RegisterScreen.jsx (both looks since v1.71): the live
 * username check (debounced 500ms), field validation, register(), the
 * onboarding-or-home route after success, and the fade-out before it.
 */
import { useEffect, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';
import { useLoading } from '../context/LoadingContext';
import { authService } from '../services/authService';
import { getInputValue } from './useSignIn';
import { CONSENT_REQUIRED_ERROR } from '../constants/legalVersion';

export const USERNAME_PATTERN = /^[a-zA-Z0-9_]{1,30}$/;
export const REGISTER_ERRORS = {
  empty: 'Please fill in all fields',
  taken: 'Username is already taken',
  invalid: 'Username must be 3+ characters, letters, numbers and underscores only',
  consent: CONSENT_REQUIRED_ERROR,
};

/** Synchronous part of the username check: null | 'invalid' | 'check'. */
export function classifyUsername(value) {
  const v = (value || '').trim();
  if (v.length < 3) return v.length > 0 ? 'invalid' : null;
  if (!USERNAME_PATTERN.test(v)) return 'invalid';
  return 'check';
}

export default function useRegister({ exitDuration = 600 } = {}) {
  const { navigateTo } = useNavigation();
  const { register } = useAuth();
  const { startLoading } = useLoading();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // 21+ and Terms/Privacy agreement: unchecked by default, required to submit.
  const [consent, setConsentState] = useState(false);
  const [consentMissing, setConsentMissing] = useState(false);
  const setConsent = (v) => { setConsentState(v); if (v) setConsentMissing(false); };
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // 'checking' | 'available' | 'taken' | 'invalid' | null
  const usernameCheckTimer = useRef(null);
  const usernameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const pageOpacity = useRef(new Animated.Value(1)).current;

  const isBusy = loading || isExiting;

  useEffect(() => () => clearTimeout(usernameCheckTimer.current), []);

  const exitAnimation = (callback) => {
    setIsExiting(true);
    Animated.timing(pageOpacity, {
      toValue: 0,
      duration: exitDuration,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      if (callback) callback();
    });
  };

  const handleUsernameChange = (value) => {
    setUsername(value);
    clearTimeout(usernameCheckTimer.current);
    const kind = classifyUsername(value);
    if (kind !== 'check') {
      setUsernameStatus(kind);
      return;
    }
    setUsernameStatus('checking');
    usernameCheckTimer.current = setTimeout(async () => {
      const available = await authService.checkUsernameAvailable(value.trim());
      if (available === null) {
        // Can't check yet (unauthenticated) — will be enforced on submit
        setUsernameStatus(null);
      } else {
        setUsernameStatus(available ? 'available' : 'taken');
      }
    }, 500);
  };

  const handleRegister = async () => {
    const resolvedUsername = getInputValue(usernameRef, username).trim();
    const resolvedEmail = getInputValue(emailRef, email).trim();
    const resolvedPassword = getInputValue(passwordRef, password);

    if (resolvedUsername === '' || resolvedEmail === '' || resolvedPassword === '') {
      setError(REGISTER_ERRORS.empty);
      return;
    }
    if (usernameStatus === 'taken') {
      setError(REGISTER_ERRORS.taken);
      return;
    }
    if (usernameStatus === 'invalid') {
      setError(REGISTER_ERRORS.invalid);
      return;
    }
    if (!consent) {
      setConsentMissing(true);
      setError(REGISTER_ERRORS.consent);
      return;
    }

    setLoading(true);
    setError('');

    const { error: registerError, warning } = await register(resolvedEmail, resolvedPassword, resolvedUsername, { consent: true });

    if (registerError) {
      setError(registerError);
      setLoading(false);
    } else {
      // Check if onboarding is currently enabled before routing there
      let onboardingEnabled = true;
      try {
        const snap = await getDoc(doc(getFirestore(), 'site_config', 'onboarding'));
        if (snap.exists() && snap.data().enabled === false) onboardingEnabled = false;
      } catch {}
      if (warning) {
        try { window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: warning } })); } catch {}
      }
      startLoading();
      exitAnimation(() => {
        navigateTo(onboardingEnabled ? 'Onboarding' : 'Home');
      });
    }
  };

  const goToLogin = () => navigateTo('Login');

  return {
    username, handleUsernameChange, usernameStatus,
    email, setEmail, password, setPassword,
    consent, setConsent, consentMissing,
    error, loading, isExiting, isBusy,
    usernameRef, emailRef, passwordRef, pageOpacity,
    handleRegister, goToLogin,
  };
}
